<!-- src/views/pages/administration/reports/bank-transfer-report.vue -->
<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li class="px-2 pb-2">
                  <label class="form-label small mb-1">Bank file format</label>
                  <select v-model="exportTemplateId" class="form-select form-select-sm">
                    <option :value="0">Standard (DoneQuick)</option>
                    <option
                      v-for="t in exportTemplates"
                      :key="t.id"
                      :value="t.id"
                    >
                      {{ templateExportLabel(t) }}
                    </option>
                  </select>
                  <p class="form-text mb-0 mt-1">{{ exportFormatHint }}</p>
                </li>
                <li><hr class="dropdown-divider" /></li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click.prevent="exportCsv"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    :class="{ 'fw-semibold': preferXlsxExport }"
                    href="javascript:void(0);"
                    @click.prevent="exportXlsx"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-spreadsheet me-1"></i>Export as Excel
                    <span v-if="preferXlsxExport" class="small text-muted"> (recommended)</span>
                  </a>
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click.prevent="exportPdf"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li><hr class="dropdown-divider" /></li>
                <li>
                  <router-link
                    class="dropdown-item rounded-1"
                    to="/payroll/bank-transfer-formats"
                  >
                    Manage bank formats…
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Filters + summary -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-3">
              <label class="form-label">Payroll Frequency</label>
              <select v-model="filters.frequency" class="form-select">
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="daily">Daily</option>
              </select>
            </div>

            <div class="col-md-3" v-if="filters.frequency === 'monthly'">
              <label class="form-label">Payroll Period</label>
              <input v-model="filters.period" type="month" class="form-control" />
            </div>

            <div class="col-md-3" v-else>
              <label class="form-label">Period Start</label>
              <input v-model="filters.period_start" type="date" class="form-control" />
            </div>

            <div class="col-md-3" v-if="filters.frequency !== 'monthly'">
              <label class="form-label">Period End</label>
              <input v-model="filters.period_end" type="date" class="form-control" />
            </div>

            <div class="col-md-3">
              <label class="form-label">Bank Name (optional)</label>
              <input
                v-model="filters.bank_name"
                type="text"
                class="form-control"
                placeholder="Filter by bank name"
              />
            </div>

            <div class="col-md-3">
              <label class="form-label">Bank Code (optional)</label>
              <input
                v-model="filters.bank_code"
                type="text"
                class="form-control"
                placeholder="e.g. 01"
              />
            </div>

            <div class="col-md-3 d-flex gap-2">
              <button
                type="button"
                class="btn btn-primary mt-auto w-100"
                :disabled="loading"
                @click="fetchReport"
              >
                <span
                  v-if="loading"
                  class="spinner-border spinner-border-sm me-1"
                ></span>
                Load
              </button>
            </div>
          </div>

          <!-- Summary -->
          <div v-if="summary" class="row mt-3 border-top pt-3 text-sm">
            <div class="col-md-3">
              <strong>Period:</strong>
              <div>{{ summary.period_label || summary.period }}</div>
            </div>
            <div class="col-md-3">
              <strong>Employees:</strong>
              <div>{{ summary.employee_count }}</div>
            </div>
            <div class="col-md-3">
              <strong>Total Amount:</strong>
              <div>
                {{ summary.currency }}
                {{
                  Number(summary.total_amount || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })
                }}
              </div>
            </div>
            <div class="col-md-3" v-if="summary.bank_name">
              <strong>Bank:</strong>
              <div>
                {{ summary.bank_name }} ({{ summary.bank_code || "-" }})
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Bank Transfer List</h5>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <a-table
              class="table datatable thead-light"
              :columns="columns"
              :data-source="rows"
              :loading="loading"
              :pagination="false"
              row-key="employee_id"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'amount'">
                  {{ record.currency }}
                  {{
                    Number(record.amount).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })
                  }}
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-page-actions {
    align-items: stretch !important;
  }

  .dq-page-actions > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-page-actions .dropdown,
  .dq-page-actions .dropdown-toggle {
    width: 100%;
  }

  .card-body .row.g-3 > div[class*="col-"] {
    width: 100%;
  }

  .table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }

  :deep(.ant-table) {
    min-width: 1100px;
  }
}
</style>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import moment from "moment";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import { clearBootstrapModalResidue } from "@/utils/bootstrapModal";

const title = "Bank Transfer Report";
const text = "Administration";
const text1 = "Bank Transfer Report";

const route = useRoute();
const store = useEmployeeSalaryStore();

const filters = reactive({
  frequency: "monthly",
  period: moment().format("YYYY-MM"),
  period_start: moment().startOf("week").format("YYYY-MM-DD"),
  period_end: moment().startOf("week").add(6, "days").format("YYYY-MM-DD"),
  bank_name: "",
  bank_code: "",
});

const loading = computed(() => store.bankTransferLoading);
const summary = computed(() => store.bankTransferSummary);
const rows = computed(() => store.bankTransferRows || []);
const exportTemplates = computed(() =>
  (store.bankTransferTemplates || []).filter((t) => t.id && !t.system),
);
const STORAGE_KEY = "dq-bank-transfer-export-template-id";
const exportTemplateId = ref(0);

const selectedExportTemplate = computed(() =>
  exportTemplates.value.find((t) => t.id === exportTemplateId.value),
);

const preferXlsxExport = computed(
  () =>
    selectedExportTemplate.value?.file_format === "xlsx" ||
    (selectedExportTemplate.value?.layout?.sparse_column_count || 0) > 0,
);

const exportFormatHint = computed(() => {
  if (exportTemplateId.value === 0) {
    return "Standard = built-in CSV columns. Custom bank layouts usually need Excel.";
  }
  const t = selectedExportTemplate.value;
  if (!t) return "";
  if (t.is_default) return "This is your company default export format.";
  if (t.filter_bank_code) return `Linked to bank code ${t.filter_bank_code} on this report.`;
  return t.file_format === "xlsx"
    ? "Use Export as Excel for this bank template."
    : "CSV matches this format’s column layout.";
});

function templateExportLabel(t) {
  let label = t.name || "Format";
  if (t.is_default) label += " · default";
  if (t.file_format === "xlsx") label += " · Excel";
  return label;
}

const columns = [
  { title: "Employee ID", dataIndex: "employee_id", key: "employee_id" },
  { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },
  { title: "Bank", dataIndex: "bank_name", key: "bank_name" },
  { title: "Bank Code", dataIndex: "bank_code", key: "bank_code" },
  { title: "Branch", dataIndex: "branch_name", key: "branch_name" },
  { title: "Branch Code", dataIndex: "branch_code", key: "branch_code" },
  {
    title: "Account Number",
    dataIndex: "account_number",
    key: "account_number",
  },
  { title: "Amount", dataIndex: "amount", key: "amount" },
  { title: "Currency", dataIndex: "currency", key: "currency" },
  { title: "Reference", dataIndex: "reference", key: "reference" },
];

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

async function fetchReport() {
  const params = buildParams();
  if (!params) return;
  const data = await store.fetchBankTransferReport({
    ...params,
    bank_name: filters.bank_name || undefined,
    bank_code: filters.bank_code || undefined,
  });
  const suggested = data?.suggested_export_template_id;
  if (filters.bank_code?.trim()) {
    if (suggested !== undefined && suggested !== null) {
      exportTemplateId.value = Number(suggested) || 0;
    }
  } else if (localStorage.getItem(STORAGE_KEY) === null && suggested) {
    exportTemplateId.value = Number(suggested) || 0;
  }
}

function exportParams() {
  const params = buildParams();
  if (!params) return null;
  return {
    ...params,
    bank_name: filters.bank_name || undefined,
    bank_code: filters.bank_code || undefined,
    template_id: exportTemplateId.value,
  };
}

function exportCsv() {
  const params = exportParams();
  if (!params) return;
  store.exportBankTransferReport("csv", params);
}

function exportXlsx() {
  const params = exportParams();
  if (!params) return;
  store.exportBankTransferReport("xlsx", params);
}

function exportPdf() {
  const params = exportParams();
  if (!params) return;
  store.exportBankTransferReport("pdf", params);
}

function buildParams() {
  if (filters.frequency === "monthly") {
    if (!filters.period) return null;
    return {
      frequency: "monthly",
      period: filters.period,
    };
  }

  if (!filters.period_start || !filters.period_end) return null;
  return {
    frequency: filters.frequency,
    period_start: filters.period_start,
    period_end: filters.period_end,
  };
}

watch(exportTemplateId, (id) => {
  localStorage.setItem(STORAGE_KEY, String(id ?? 0));
});

function applyRouteQueryToFilters() {
  const q = route.query;
  const pick = (key) => {
    const v = q[key];
    return typeof v === "string" && v.trim() !== "" ? v.trim() : null;
  };
  const frequency = pick("frequency");
  if (frequency) {
    filters.frequency = frequency;
  }
  const period = pick("period");
  if (period) {
    filters.period = period;
  }
  const periodStart = pick("period_start");
  if (periodStart) {
    filters.period_start = periodStart;
  }
  const periodEnd = pick("period_end");
  if (periodEnd) {
    filters.period_end = periodEnd;
  }
  const bankCode = pick("bank_code");
  if (bankCode) {
    filters.bank_code = bankCode;
  }
  const bankName = pick("bank_name");
  if (bankName) {
    filters.bank_name = bankName;
  }
}

onMounted(() => {
  clearBootstrapModalResidue();
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved !== null && saved !== "") {
    exportTemplateId.value = Number(saved) || 0;
  }
  applyRouteQueryToFilters();
  fetchReport();
});
</script>


